import React from 'react';
import { useContent } from '../cms/content.js';

const GOLD_GRADIENT = {
  background: 'linear-gradient(160deg, #f6e0a6 0%, #e8c47e 44%, #cfa35d 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent'
};

export default function Hero() {
  const cms = useContent();
  const boxes = cms.hero.boxes || [];

  return (
    <header
      id="top"
      style={{
        position: 'relative',
        zIndex: 10,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        padding: '120px 24px 140px'
      }}
    >

      {/* Main tagline */}
      <h1
        data-reveal=""
        style={{
          margin: 0,
          fontFamily: "'Bodoni Moda', serif",
          fontStyle: 'italic',
          fontWeight: 300,
          fontSize: 'clamp(40px, 6vw, 96px)',
          lineHeight: 1.05,
          letterSpacing: '-0.02em',
          maxWidth: '900px',
          ...GOLD_GRADIENT
        }}
      >
        {cms.hero.headingAccent}
      </h1>

      {/* Subhead */}
      <p
        data-reveal=""
        style={{
          margin: '24px auto 0',
          maxWidth: '680px',
          fontSize: '17px',
          lineHeight: 1.6,
          fontWeight: 300,
          color: '#b2ab9f',
          fontFamily: "'Raleway', sans-serif"
        }}
      >
        {cms.hero.subhead}
      </p>

      {/* Journey boxes */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: '22px',
          width: '100%',
          maxWidth: '1180px',
          margin: '56px auto 0'
        }}
      >
        {boxes.map((box, index) => (
          <article
            key={box.level}
            data-reveal=""
            className="kny-card kny-hero-box"
            style={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'left',
              position: 'relative',
              isolation: 'isolate',
              minHeight: '500px',
              padding: '0',
              border: index === 1
                ? '1px solid rgba(201,168,106,0.4)'
                : '1px solid rgba(201,168,106,0.2)',
              borderRadius: '8px',
              background: index === 1
                ? 'linear-gradient(175deg, rgba(22,23,28,0.97) 0%, rgba(14,15,20,0.98) 40%, rgba(10,10,11,0.99) 100%)'
                : 'linear-gradient(170deg, rgba(18,19,22,0.96) 0%, rgba(12,13,16,0.98) 50%, rgba(10,10,11,0.99) 100%)',
              overflow: 'hidden',
              boxShadow: index === 1
                ? '0 8px 32px -12px rgba(201,168,106,0.12), 0 0 0 1px rgba(201,168,106,0.08) inset'
                : 'none'
            }}
          >
            {/* Top accent bar — brighter on featured card */}
            <div
              style={{
                height: '4px',
                width: '100%',
                background: index === 1
                  ? 'linear-gradient(90deg, rgba(201,168,106,0.2), #c9a86a 30%, #e8c47e 70%, rgba(201,168,106,0.2))'
                  : 'linear-gradient(90deg, rgba(201,168,106,0.1), rgba(201,168,106,0.35), rgba(201,168,106,0.1))',
                flexShrink: 0
              }}
            />

            <div
              style={{
                padding: 'clamp(32px, 3.4vw, 50px) clamp(26px, 2.8vw, 40px)',
                display: 'flex',
                flexDirection: 'column',
                flex: 1
              }}
            >
              {/* Eyebrow label */}
              <div
                style={{
                  font: "500 10px/1 'JetBrains Mono', monospace",
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: index === 1 ? '#c9a86a' : '#6f6a60',
                  marginBottom: '18px'
                }}
              >
                {box.level} · Service Tier
              </div>

              {/* Title */}
              <h3
                style={{
                  margin: '0 0 14px',
                  fontFamily: "'Cormorant', serif",
                  fontWeight: 500,
                  fontSize: 'clamp(28px, 2.8vw, 40px)',
                  lineHeight: 1.04,
                  color: '#f5f0e6',
                  letterSpacing: '-0.01em'
                }}
              >
                {box.title}
              </h3>

              {/* Gold divider */}
              <div
                style={{
                  width: '32px',
                  height: '1px',
                  background: index === 1 ? '#c9a86a' : 'rgba(201,168,106,0.3)',
                  marginBottom: '18px'
                }}
              />

              {/* Description */}
              <p
                style={{
                  margin: 0,
                  fontSize: '15px',
                  lineHeight: 1.72,
                  fontWeight: 300,
                  color: '#a8a296'
                }}
              >
                {box.description}
              </p>

              {/* CTA — pinned to card bottom */}
              <div style={{ marginTop: 'auto', paddingTop: '40px' }}>
                <a
                  href={box.ctaHref}
                  className="kny-cta kny-hero-cta"
                  aria-label={`${box.ctaLabel} — ${box.title}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '18px 22px',
                    borderRadius: '4px',
                    background: index === 1
                      ? 'linear-gradient(135deg, #f6e0a6 0%, #e8c47e 50%, #cfa35d 100%)'
                      : 'rgba(201,168,106,0.06)',
                    color: index === 1 ? '#0a0a0b' : '#c9a86a',
                    border: index === 1
                      ? 'none'
                      : '1px solid rgba(201,168,106,0.3)',
                    textAlign: 'center',
                    textDecoration: 'none',
                    font: "600 11px/1 'JetBrains Mono', monospace",
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    transition: 'all 0.4s cubic-bezier(0.2, 0.7, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {box.ctaLabel}
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>
    </header>
  );
}
